import * as React from "react";
import {
  DefaultButton,
  MessageBar,
  MessageBarType,
  Pivot,
  PivotItem,
  Stack,
  Separator,
  Spinner,
  Text,
} from "@fluentui/react";
import useAxios from "axios-hooks";
import ActionItemsStep from "./ActionItemsStep";
import BasicInfoStep from "./BasicInfoStep";
import { useCallback, useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { initialValues } from "../BasicInfo/BasicInfoFields";
import { Formik } from "formik";
import basicInfoSchema from "../BasicInfo/basicInfoValidationSchema";
import ChecklistPreviewPage from "../../Preview/CheckList/ChecklistPreviewPage";
import OnOffStep from "./OnOffStep";
import PrimaryButton from "../../../../Forms/PrimaryButton";
import { isChecklistPublished } from "../../../../../util";

const extractErrorMessages = (error) => {
  const data = error?.response?.data;
  if (!data) return [error.message || "An unexpected error occurred."];

  // Handle custom Response format: { errors: [{ errorMessage: "..." }] }
  if (Array.isArray(data.errors)) {
    return data.errors.map((e) => e.errorMessage || e.ErrorMessage || JSON.stringify(e));
  }

  // Handle ASP.NET ValidationProblemDetails: { errors: { "Prop": ["msg"] } }
  if (data.errors && typeof data.errors === "object") {
    return Object.values(data.errors).flat();
  }

  if (data.title) return [data.title];
  if (typeof data === "string") return [data];

  return ["An unexpected error occurred."];
};

export const CreateChecklistWizard = () => {
  const history = useHistory();
  let { checklistId: checklistIdFromParams } = useParams();

  const [{ data }, submitForm] = useAxios(
    { url: `checklist` },
    { manual: true }
  );

  const [
    { data: checklistData, loading: getActionItemLoading },
    getChecklistData,
  ] = useAxios(
    { url: `checklist/${checklistIdFromParams}`, method: "get" },
    { manual: true }
  );

  const checklistId = (data && data.checklistID) || checklistIdFromParams;

  const [selectedKey, setSelectedKey] = React.useState(0);

  const nextOnClick = useCallback(() => {
    setSelectedKey(Number(selectedKey) + 1);
  }, [selectedKey]);

  const prevOnClick = () => {
    setSelectedKey(Number(selectedKey) - 1);
  };

  const handleLinkClick = (item) => {
    const itemKey = item.props.itemKey;
    setSelectedKey(itemKey);
  };

  useEffect(() => {
    if (checklistIdFromParams && !checklistData && !getActionItemLoading) {
      getChecklistData();
    }
  }, [
    checklistData,
    checklistIdFromParams,
  ]);

  // Silently refetch checklist data when switching tabs so action item counts stay current
  useEffect(() => {
    if (checklistIdFromParams && checklistData) {
      getChecklistData();
    }
  }, [selectedKey]);

  if (getActionItemLoading && !checklistData) {
    return <Spinner className="absolute-center" text="Loading Checklist..." />;
  }

  if (checklistIdFromParams && !checklistData && !getActionItemLoading) {
    return <Text>Sorry, this checklist no longer exist</Text>;
  }

  return (
    <Formik
      initialValues={mapInitialValues(checklistData)}
      onSubmit={async (values, { setStatus }) => {
        setStatus(undefined);

        // Prevent publishing if there are no action items
        if (values.status === "Published") {
          const hasActionItems = checklistData?.actionItems?.length > 0;
          if (!hasActionItems) {
            setStatus({ errors: ["Cannot publish a checklist without action items. Please add action items first."] });
            return;
          }

          // Prevent publishing with "Required Only" completion rule if no action items are set to Required
          if (values.completionRules === "Required Only") {
            const hasRequiredItems = checklistData.actionItems.some(
              (item) => item.requirement === "Required"
            );
            if (!hasRequiredItems) {
              setStatus({ errors: ["Cannot publish with 'Required Only' completion rule when no action items are set to Required. Please mark at least one action item as Required or change the completion rule."] });
              return;
            }
          }
        }

        try {
          const isCreate = !checklistData;
          const response = await submitForm({
            data: mapValuesForSubmit(values),
            method: checklistData ? "put" : "post",
            url: `checklist/${
              (checklistData && checklistData.checklistID) || ""
            }`,
          });

          // After creating a new checklist, update the URL so the form switches to edit mode
          if (isCreate && response?.data?.checklistID) {
            history.replace(`/checklist-feature/checklists/${response.data.checklistID}/wizard`);
          }

          nextOnClick();
        } catch (e) {
          console.error(e);
          setStatus({ errors: extractErrorMessages(e) });
        }
      }}
      isInitialValid={false}
      validationSchema={basicInfoSchema}
    >
      {({ getFieldMeta, status, setStatus }) => {
        const isPublished = isChecklistPublished(
          getFieldMeta("status").initialValue,
          getFieldMeta("publishedDate").initialValue
        );
        return (
          <>
            {status?.errors && (
              <MessageBar
                messageBarType={MessageBarType.error}
                isMultiline
                onDismiss={() => setStatus(undefined)}
              >
                {status.errors.map((msg, i) => (
                  <div key={i}>{msg}</div>
                ))}
              </MessageBar>
            )}
            <Pivot
              selectedKey={String(selectedKey)}
              onLinkClick={handleLinkClick}
            >
              <PivotItem headerText="Basic Info" itemKey="0">
                <BasicInfoStep
                  prevOnClick={prevOnClick}
                  isUpdate={!!checklistData}
                  isPublished={isPublished}
                  checklistId={checklistId}
                />
              </PivotItem>
              {!isPublished && (
                <PivotItem headerText="Action Items" itemKey="1">
                  {checklistId && (
                    <ActionItemsStep
                      checklistId={checklistId}
                      prevOnClick={prevOnClick}
                      nextOnClick={nextOnClick}
                    />
                  )}
                </PivotItem>
              )}
              <PivotItem headerText="On Off Settings" itemKey="2">
                {checklistId && (
                  <OnOffStep
                    checklistId={checklistId}
                    prevOnClick={prevOnClick}
                    nextOnClick={nextOnClick}
                  />
                )}
              </PivotItem>
              {checklistId && (
                <PivotItem headerText="Checklist Preview" itemKey="3">
                  {checklistId && (
                    <>
                      <ChecklistPreviewPage checklistId={checklistId} />
                      <NavigationalButtons
                        width="75%"
                        onNextHandler={async () => {
                          history.push("/checklist-feature/checklists/");
                        }}
                        onNextProps={{ text: "Finish" }}
                        prevOnClick={prevOnClick}
                      />
                    </>
                  )}
                </PivotItem>
              )}
            </Pivot>
          </>
        );
      }}
    </Formik>
  );
};

export function mapInitialValues(checklistData) {
  if (checklistData)
    return {
      label:checklistData.label,
      name: checklistData.name,
      header: checklistData.header,
      subheader: checklistData.subheader,
      actionOrder: checklistData.actionOrder,
      completionRules: checklistData.completionRules,
      completeAny: checklistData.completeAny,
      completionMessage: checklistData.completionMessage,
      status: checklistData.status,
      slug: checklistData.slug,
      publishedDate: new Date(checklistData.publishedDate),
      completionCert: checklistData.completionCert
    };
  else return initialValues;
}

function mapValuesForSubmit(values) {
  return { ...values, isActive: true };
}

export const NavigationalButtons = ({
  onNextHandler,
  onNextProps,
  onPrevHandler,
  onPrevProps,
  width,
  loading,
}) => {
  const showPrevious = onPrevHandler || onPrevProps;
  return (
    <>
      <Separator />
      <Stack
        horizontal
        styles={{
          root: {
            height: 50,
            ...(width && { width }),
          },
        }}
        horizontalAlign={showPrevious ? "space-between" : "end"}
        verticalAlign="center"
      >
        {showPrevious && (
          <DefaultButton onClick={onPrevHandler} {...onPrevProps}>
            {(onPrevProps && onPrevProps.text) || "Prev"}
          </DefaultButton>
        )}
        <PrimaryButton
          text={(onNextProps && onNextProps.text) || "Next"}
          loading={loading}
          onClick={onNextHandler}
          {...onNextProps}
        />
      </Stack>
    </>
  );
};
export default CreateChecklistWizard;
